import { axiosInstance } from './axiosManager'

// Instructors who can run an experiment besides its creator (backend: experimentService
// listInstructors / addInstructors / removeInstructor): they can Start, Pause, Resume, Reset
// and End it and find it in their session list. Only the creator changes the list.
// Every call resolves, to { ok: true, ... } or { ok: false, status, message } - message is the
// server's own ({ message }, e.g. "No instructor account signs in with that email.") for a
// refusal; status is undefined when the server did not answer.

const experimentUrl = process.env.REACT_APP_HOST_API + '/experiments'

// An added account's kind, mid-sentence: 'instructor' is an instructor-table account, 'user' a
// users-table account. Either kind can sign in with a password, on the same sign-in form, so
// the label names the kind only. The list names accounts, not people: an instructor runs the
// experiment only while signed in with an account on it.
const ACCOUNT_KINDS = {
  instructor: 'instructor account',
  user: 'user account',
}
export const accountKindLabel = (kind) => ACCOUNT_KINDS[kind] || (kind ? `${kind} account` : 'account')

// Whether every one of these accounts (those an Add by email just added) is an instructor
// account: the person may sign in with another account, which cannot run it yet.
export const onlyInstructorAccounts = (accounts) =>
  accounts.length > 0 && accounts.every((account) => account.kind === 'instructor')

const failure = (error, fallback) => {
  const status = error?.response?.status
  const serverMessage = error?.response?.data?.message
  let message = fallback
  if (!status) message = 'Could not reach the server. Check your connection and try again.'
  // A 5xx message is the server's internal error, not something to show.
  else if (status < 500 && typeof serverMessage === 'string' && serverMessage) message = serverMessage
  else if (status === 401) message = 'Sign in again as an instructor, then try again.'
  return { ok: false, status, message }
}

const listOf = (data) => (Array.isArray(data?.instructors) ? data.instructors : [])

// Who runs the experiment: { ok: true, creatorId, instructors: [{ id, kind, name, addedAt }],
// canChange } - canChange is true for its creator only.
export const getExperimentInstructors = async (experimentId) => {
  try {
    const response = await axiosInstance.get(`${experimentUrl}/${experimentId}/instructors`)
    return {
      ok: true,
      creatorId: response.data?.creatorId ?? null,
      instructors: listOf(response.data),
      canChange: Boolean(response.data?.canChange),
    }
  } catch (error) {
    return failure(error, 'Could not load who runs this experiment. Try again in a moment.')
  }
}

// Adds every instructor account that signs in with `email` (one person can have more than
// one: users-table and instructor-table accounts). Resolves to { ok: true, instructors }, the
// updated list. The server looks the email up among all accounts, so it gets more time than
// the shared instance's 10 s default.
export const addExperimentInstructor = async (experimentId, email) => {
  try {
    const response = await axiosInstance.post(
      `${experimentUrl}/${experimentId}/instructors`,
      { email },
      { timeout: 20000 }
    )
    return { ok: true, instructors: listOf(response.data) }
  } catch (error) {
    return failure(error, 'Could not add the instructor. Try again in a moment.')
  }
}

// Removes one added account ({ id, kind } as the list gives it). Resolves to
// { ok: true, instructors }, the updated list.
export const removeExperimentInstructor = async (experimentId, { id, kind }) => {
  try {
    const response = await axiosInstance.delete(
      `${experimentUrl}/${experimentId}/instructors/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`
    )
    return { ok: true, instructors: listOf(response.data) }
  } catch (error) {
    return failure(error, 'Could not remove the instructor. Try again in a moment.')
  }
}
